/**
 * Network-wide stats (presentational) — the COMMUNITY half of the Stats tab,
 * complementing the personal {@link import('./StatsScreen').StatsScreen}. Draws the
 * server-maintained {@link NetworkStats} aggregate for one game: the recent average
 * score (a rolling window, so it tracks current form), total runs, the network
 * best, players tracked, and the live "players online" count.
 *
 * Presentational only: the shell fetches `services.getNetworkStats(gameId)` (public
 * read) and hands it in; this component computes nothing and talks to no service.
 */
import type { NetworkStats } from '../services';
import { DeathBars, DeathHeatMap } from './StatsScreen';
import { useT } from './i18n';

interface NetworkStatsViewProps {
  stats: NetworkStats;
  /** The live presence count (from the shell's online subscription). */
  onlineCount: number;
  /** Display name of the game these numbers belong to (for the caption). */
  gameName: string;
  loading?: boolean;
}

/** One big network number with a caption. */
function NetCell({ value, label, hero }: { value: string; label: string; hero?: boolean }) {
  return (
    <div className={'net-cell' + (hero ? ' net-hero' : '')}>
      <span className="net-value">{value}</span>
      <span className="net-label">{label}</span>
    </div>
  );
}

export function NetworkStatsView({ stats, onlineCount, loading }: NetworkStatsViewProps) {
  const t = useT();
  return (
    <section className="panel network-stats">
      <style>{NETWORK_STATS_CSS}</style>
      <h2>{t('net.heading')}</h2>
      <p className="network-stats-sub">
        {t('net.sub')}
      </p>

      {loading ? (
        <p className="network-stats-status">{t('net.loading')}</p>
      ) : (
        <>
          <div className="net-grid">
            <NetCell
              hero
              value={stats.recentAvgScore.toLocaleString()}
              label={
                stats.recentWindow > 0
                  ? `${t('net.recentAverage')} · ${stats.recentWindow.toLocaleString()}`
                  : t('net.recentAverage')
              }
            />
            <NetCell
              value={stats.hourlyAvgScore > 0 ? stats.hourlyAvgScore.toLocaleString() : '—'}
              label={t('net.hourlyAvg')}
            />
            <NetCell value={onlineCount.toLocaleString()} label={t('net.playersOnline')} />
            <NetCell value={stats.totalRuns.toLocaleString()} label={t('net.totalRuns')} />
            <NetCell value={stats.bestScore.toLocaleString()} label={t('net.networkBest')} />
            <NetCell value={stats.playersTracked.toLocaleString()} label={t('net.playersTracked')} />
          </div>

          {/* What the whole network is dying to, and where. */}
          <h3 className="net-section">{t('net.deadliest')}</h3>
          <DeathBars deaths={stats.deathsByFamily} />
          <h3 className="net-section">{t('net.heatTitle')}</h3>
          <DeathHeatMap heat={stats.deathHeat} emptyLabel={t('stats.heatEmpty')} />
        </>
      )}
    </section>
  );
}

const NETWORK_STATS_CSS = `
.network-stats { width: min(440px, 92vw); }
.network-stats .network-stats-sub {
  margin: 0 0 14px;
  color: var(--muted);
  font-size: 13px;
}
.network-stats .network-stats-status { color: var(--muted); margin: 0; }
.network-stats .net-section {
  margin: 18px 0 10px;
  font-size: 12px;
  font-weight: 800;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.network-stats .net-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.network-stats .net-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 16px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
}
.network-stats .net-hero {
  grid-column: 1 / -1;
  background: linear-gradient(135deg, var(--accent-tint), var(--panel-2));
  border-color: var(--glass-border-strong);
}
.network-stats .net-hero .net-value {
  font-size: 38px;
  background: linear-gradient(96deg, var(--accent), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.network-stats .net-value {
  font-size: 24px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  line-height: 1.1;
}
.network-stats .net-label {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
@media (max-width: 380px) {
  .network-stats .net-grid { grid-template-columns: 1fr; }
}
`;
